import OktaAuth from '@okta/okta-auth-js';
import UserSessionService from 'services/navigation/userSession.service';
import { notifyError } from 'helpers/errorNotifier';
import { IS_PRODUCTION_MODE, publicEnvConfig } from 'helpers/env';
import { createAbsoluteUrlFromPath } from 'helpers/url';
import { withCookiesRestClient } from 'helpers/restclient';

const OKTA_ACTIVE_STATUS_ENUM = 'ACTIVE';
const OKTA_SUCCESS_TRANSACTION_ENUM = 'SUCCESS';
const OKTA_SCOPES = ['openid', 'profile', 'email', 'offline_access']; // offline_access is needed to get refresh token
const GENERIC_ERROR_RESPONSE = {
	success: false,
	errorMessage: 'There is an error trying to login with Okta, try again or contact support',
};

const isAfcOktaFastPathResponse = (responseOrError) => {
	const data = responseOrError?.data || responseOrError?.error;
	return !data?.success && data?.data?.afc_okta_fast_path;
};

export default class OktaService {
	static instance;

	static getInstance() {
		if (!this.instance) this.instance = new OktaService();
		return this.instance;
	}

	constructor() {
		this.oktaAuth = new OktaAuth({
			issuer: publicEnvConfig.OKTA_ISSUER,
			clientId: publicEnvConfig.OKTA_CLIENT_ID,
			devMode: !IS_PRODUCTION_MODE,
			redirectUri: createAbsoluteUrlFromPath('/sign_in'),
			postLogoutRedirectUri: createAbsoluteUrlFromPath('/sign_in'),
			pkce: true,
			responseType: 'code',
			scopes: OKTA_SCOPES,
			useRefreshTokens: true,
			tokenManager: {
				storage: 'memory',
				// storage: 'localStorage', // By doing that it will persist is okta-token-storage (same is using Canada) explore cookies option if needed
				// syncStorage: true, // for local storage sync between tabs
				autoRenew: true,
				autoRemove: true,
			},
		});
		this.withCookiesRestClient = withCookiesRestClient;
		this.startOktaAuthService();
	}
	// refresh token storage options:
	// 	memory keeps tokens only in JS memory; closing/reloading the browser wipes them. After restart it can only recover if the Okta session cookie is still valid, otherwise silent reauth fails.
	// localStorage persists tokens across reloads; after restart the SDK can continue using the stored refresh token to mint new access tokens even if the Okta session is gone (as long as refresh token policy allows it).
	// Okta “inactivity” settings apply to the Okta session cookie, not to in‑memory tokens. That’s why memory doesn’t honor multi‑day inactivity across restarts.
	// Security tradeoff: memory is safer against XSS; localStorage (or non‑HttpOnly cookies) increases persistence but exposes tokens to XSS.

	startOktaAuthService = () => {
		if (typeof window === 'undefined') return;
		if (typeof this.oktaAuth?.start !== 'function') return;
		this.oktaAuth
			.start()
			.then(async () => {
				// await this.getUpdatedAccessToken(); // needed if we want to handle refresh tokens using memory storage
				// await this.logSessionAndTokens();
			})
			.catch((error) => {
				notifyError(error, { method: 'OktaService.startOktaAuthService' });
			});
	};

	// logSessionAndTokens = async () => {
	// 	if (IS_PRODUCTION_MODE) return;
	// 	try {
	// 		const session = await this.oktaAuth.session.get();
	// 		const hasSession = session?.status === OKTA_ACTIVE_STATUS_ENUM;
	// 		const refreshToken = await this.oktaAuth.tokenManager.get('refreshToken');
	// 		const accessToken = await this.oktaAuth.tokenManager.get('accessToken');
	// 		console.info('[OktaService] session/tokens', {
	// 			hasSession,
	// 			hasRefreshToken: Boolean(refreshToken?.refreshToken),
	// 			hasAccessToken: Boolean(accessToken?.accessToken),
	// 		});
	// 	} catch (error) {
	// 		notifyError(error, { method: 'OktaService.logSessionAndTokens' });
	// 	}
	// };

	loginUsingOurForm = async (email, password, queryParamsToForward) => {
		let tokenResponse;
		try {
			const transaction = await this.oktaAuth.signInWithCredentials({ username: email, password });
			if (transaction.status !== OKTA_SUCCESS_TRANSACTION_ENUM) return GENERIC_ERROR_RESPONSE;
			// Start a session using the sessionToken or redirect
			const sessionToken = transaction.sessionToken;

			tokenResponse = await this.oktaAuth.token.getWithoutPrompt({
				sessionToken,
				scopes: OKTA_SCOPES,
			});
			this.oktaAuth.tokenManager.setTokens(tokenResponse.tokens);
		} catch (error) {
			try {
				// As login has handled on Frontend, we need to notify backend about failed login attempt
				await UserSessionService.instance.oktaLoginFailTracking({ email });
			} catch (error) {
				notifyError(error, { metadata: { email } });
			}
			notifyError(error, { metadata: { email } });
			return GENERIC_ERROR_RESPONSE;
		}

		try {
			const response = await UserSessionService.instance.signIn(
				'',
				'',
				queryParamsToForward,
				tokenResponse.tokens.accessToken.accessToken,
			);
			if (isAfcOktaFastPathResponse(response)) {
				return { success: false, afcOktaFastPath: true };
			}
		} catch (error) {
			if (isAfcOktaFastPathResponse(error)) {
				return { success: false, afcOktaFastPath: true };
			}
			return { success: false, errorMessage: error?.error?.message || GENERIC_ERROR_RESPONSE.errorMessage };
		}

		return { success: true };
	};

	logout = async () => {
		try {
			this.oktaAuth?.tokenManager?.clear();
			const wasSignedOut = await this.oktaAuth.signOut({
				revokeAccessToken: true,
				revokeRefreshToken: true,
				clearTokensBeforeRedirect: true,
			});
			return wasSignedOut;
		} catch (error) {
			notifyError(error, { method: 'OktaService.logout' });
			return false;
		}
	};

	checkSession = async () => {
		const session = await this.oktaAuth.session.get();
		let isActiveSession = session.status === OKTA_ACTIVE_STATUS_ENUM;
		let userProfile = {};
		if (isActiveSession) {
			try {
				const user = await session.user();
				const { email, firstName, lastName } = user.profile;
				userProfile = { email, firstName, lastName };
			} catch (error) {
				isActiveSession = false;
				notifyError(error, { method: 'OktaService.checkSession' });
			}
		}
		return { isActiveSession, userProfile };
	};

	generateNewCodeForActiveSessions = async ({ queryParamsToForward }) => {
		try {
			const tokenResponse = await this.oktaAuth.token.getWithoutPrompt({
				scopes: OKTA_SCOPES,
			});
			this.oktaAuth.tokenManager.setTokens(tokenResponse.tokens);
			const response = await UserSessionService.instance.signIn(
				'',
				'',
				queryParamsToForward,
				tokenResponse.tokens.accessToken.accessToken,
			);
			if (isAfcOktaFastPathResponse(response)) {
				return { success: false, afcOktaFastPath: true };
			}
			return { success: true };
		} catch (error) {
			if (isAfcOktaFastPathResponse(error)) {
				return { success: false, afcOktaFastPath: true };
			}
			notifyError(error);
			return { success: false, errorMessage: error?.error?.message || GENERIC_ERROR_RESPONSE.errorMessage };
		}
	};

	// All this code is not needed if we are going to use tokenManager with localStorage.
	getUpdatedAccessToken = async () => {
		let isThereAnExpiredToken = false;
		try {
			const cachedToken = await this.oktaAuth.tokenManager.get('accessToken');
			if (cachedToken?.accessToken) {
				const hasExpired = this.oktaAuth.tokenManager.hasExpired(cachedToken);

				if (!hasExpired) {
					return { success: true, accessToken: cachedToken.accessToken, isThereAnExpiredToken };
				} else {
					isThereAnExpiredToken = true;
				}
			}
		} catch (error) {
			notifyError(error, { method: 'OktaService.getUpdatedAccessToken', stage: 'tokenManager.get' });
		}

		try {
			const tokenResponse = await this.oktaAuth.token.getWithoutPrompt({
				scopes: OKTA_SCOPES,
			});
			this.oktaAuth.tokenManager.setTokens(tokenResponse.tokens);
			return { success: true, accessToken: tokenResponse.tokens.accessToken.accessToken, isThereAnExpiredToken };
		} catch (error) {
			notifyError(error, { method: 'OktaService.getUpdatedAccessToken' });
			return {
				success: false,
				errorMessage: error?.error?.message || GENERIC_ERROR_RESPONSE.errorMessage,
				isThereAnExpiredToken,
			};
		}
	};

	checkIfAfcUser = async (email) => {
		try {
			const url = `${publicEnvConfig.RESTURL_BASE}/api/okta_users/${encodeURIComponent(email)}`;
			const response = await this.withCookiesRestClient.get(url);
			return {
				success: response?.data?.data?.afc_user,
				firstName: response?.data?.data?.first_name,
				lastName: response?.data?.data?.last_name,
			};
		} catch (error) {
			return {
				success: false,
				errorMessage: error?.error?.message,
			};
		}
	};

	getRailsOktaToken = async () => {
		try {
			const response = await this.withCookiesRestClient.get('/api/okta/token');
			return { success: true, token: response?.data?.token };
		} catch (error) {
			notifyError(error, { method: 'OktaService.getRailsOktaToken' });
			return { success: false, errorMessage: error?.error?.message || GENERIC_ERROR_RESPONSE.errorMessage };
		}
	};
}
